import { action, runInAction, observable, computed } from 'mobx';
import axios from 'axios';
import qs from 'qs';

import AssetModel from './AssetModel';
import AssetLocationModel from './AssetLocationModel';
import AssetPreviewModel from './AssetPreviewModel';

import DefaultValues from '../../../AppDefaults';

const ObjectPathbyString = (o, s) => {
    s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    s = s.replace(/^\./, ''); // strip a leading dot
    const a = s.split('.');
    for (let i = 0, n = a.length; i < n; i += 1) {
        const k = a[i];
        if (k in o) {
            o = o[k];
        } else {
            return;
        }
    }
    return o;
};

export default class AssetStore {
    @observable fetching = false;

    @observable fetchingArtworkReplacements = false;

    @observable fetchingMetadata = false;

    @observable fetchingStillPreviews = false;

    @observable fetchingAudioPreviews = false;

    @observable fetchingRelated = false;

    @observable fetchingLocations = false;

    @observable fetched = false;

    @observable fetchedArtworkReplacements = false;

    @observable fetchedMetadata = false;

    @observable fetchedStillPreviews = false;

    @observable fetchedAudioPreviews = false;

    @observable fetchedRelated = false;

    @observable fetchedLocations = false;

    @observable asset = {};

    @observable assetArtworkReplacements = [];

    @observable assetMetadataReplacements = [];

    @observable assetStillPreviews = [];

    @observable assetAudioPreviews = [];

    @observable assets = [];

    @observable related = [];

    @observable locations = [];

    @observable totalCount = 0;

    @observable limit = DefaultValues.limit;

    @observable page = 0;

    @observable query = null;

    @observable type = null;

    @observable queryRelated = null;

    @observable queryNotRelated = null;

    auth = null;

    servicesStore = null;

    constructor(auth) {
        // Set auth
        this.auth = auth;
    }

    @computed
    get totalPageCount() {
        return Math.ceil(this.totalCount / this.limit);
    }

    @computed
    get filtered() {
        if (this.query) {
            const filteredList = this.assets.filter((t) => {
                const fieldValuesMatch = this.columnsConfig
                    .filter((f) => f.type === 'metadata')
                    .map((f) => f.location)
                    .map((fp) => ObjectPathbyString(t, fp))
                    .map((val) => !!val && val.toLowerCase().indexOf(this.query.toLowerCase()) >= 0)
                    .filter((f) => f);
                if (fieldValuesMatch && fieldValuesMatch.length) {
                    // If any of the meta fields provides the query, return true
                    return true;
                }
                // Default search in the name of the element
                return t.name.toLowerCase().indexOf(this.query.toLowerCase()) >= 0;
            });
            return filteredList;
        }
        // TODO mark query as wrong and show all anyways
        // if (filteredList.length) {
        //     return filteredList;
        // }
        return this.assets;
    }

    @computed
    get assetArtworkReplacementsFiltered() {
        return this.assetArtworkReplacements.filter((x) => x);
    }

    @computed
    get columnsConfig() {
        // TODO load this from database and user config
        if (this.type) {
            switch (this.type) {
                case 'video':
                    return [
                        {
                            name: 'Thumbnail',
                            i18n: 'Assets.Thumbnail',
                            type: 'thumbnail',
                            size: 70,
                        },
                        {
                            name: 'Type',
                            i18n: 'Assets.Type',
                            type: 'type',
                        },
                        {
                            name: 'CartNo',
                            i18n: 'Assets.Video.CartNo',
                            type: 'metadata',
                            location: 'metadata.CartNo',
                        },
                        // {
                        //     name: 'Name', i18n: 'Assets.Name', type: 'metadata', location: 'name',
                        // },
                        {
                            name: 'Show',
                            i18n: 'Assets.Video.Show',
                            type: 'metadata',
                            location: 'metadata.Show',
                        },
                        {
                            name: 'Title',
                            i18n: 'Assets.Video.Title',
                            type: 'metadata',
                            location: 'metadata.Title',
                        },
                        {
                            name: 'Duration',
                            i18n: 'Assets.Duration',
                            type: 'metadata',
                            location: 'metadata.Duration',
                        },
                    ];
                case 'still':
                    return [
                        {
                            name: 'Thumbnail',
                            i18n: 'Assets.Thumbnail',
                            type: 'thumbnail',
                            size: 70,
                        },
                        {
                            name: 'Type',
                            i18n: 'Assets.Type',
                            type: 'type',
                        },
                        {
                            name: 'Name',
                            i18n: 'Assets.Name',
                            type: 'metadata',
                            location: 'name',
                        },
                        {
                            name: 'Album',
                            i18n: 'Assets.Music.Album',
                            type: 'metadata',
                            location: 'metadata.Album',
                        },
                        {
                            name: 'Created',
                            i18n: 'Assets.Created',
                            type: 'metadata',
                            location: 'createdAt',
                        },
                    ];
                case 'audio':
                    return [
                        {
                            name: 'Thumbnail',
                            i18n: 'Assets.Thumbnail',
                            type: 'thumbnail',
                            size: 70,
                        },
                        {
                            name: 'Type',
                            i18n: 'Assets.Type',
                            type: 'type',
                        },
                        {
                            name: 'Artist',
                            i18n: 'Assets.Music.Artist',
                            type: 'metadata',
                            location: 'metadata.Artist',
                        },
                        {
                            name: 'Title',
                            i18n: 'Assets.Music.Title',
                            type: 'metadata',
                            location: 'metadata.Title',
                        },
                        {
                            name: 'Album',
                            i18n: 'Assets.Music.Album',
                            type: 'metadata',
                            location: 'metadata.Album',
                        },
                        {
                            name: 'ISRC',
                            i18n: 'Assets.Music.ISRC',
                            type: 'metadata',
                            location: 'metadata.ISRC',
                        },
                        {
                            name: 'Created',
                            i18n: 'Assets.Created',
                            type: 'metadata',
                            location: 'createdAt',
                        },
                    ];
                default:
                    // returns below
                    break;
            }
        }
        // default
        return [
            {
                name: 'Thumbnail',
                i18n: 'Assets.Thumbnail',
                type: 'thumbnail',
                size: 70,
            },
            {
                name: 'Type',
                i18n: 'Assets.Type',
                type: 'type',
            },
            {
                name: 'Name',
                i18n: 'Assets.Name',
                type: 'metadata',
                location: 'name',
            },
            {
                name: 'Created',
                i18n: 'Assets.Created',
                type: 'metadata',
                location: 'createdAt',
            },
        ];
    }

    @computed
    get relatedColumnsConfig() {
        // TODO load this from database and user config
        return [
            {
                name: 'Thumbnail',
                i18n: 'Assets.Thumbnail',
                type: 'thumbnail',
                size: 70,
            },
            {
                name: 'Relation',
                i18n: 'Assets.Relation',
                type: 'metadata',
                location: 'relation',
            },
            {
                name: 'Type',
                i18n: 'Assets.Type',
                type: 'type',
            },
            // {
            //     name: 'Name', i18n: 'Assets.Name', type: 'metadata', location: 'name',
            // },
            {
                name: 'Artist',
                i18n: 'Assets.Music.Artist',
                type: 'metadata',
                location: 'metadata.Artist',
            },
            // {
            //     name: 'Title', i18n: 'Assets.Music.Title', type: 'metadata', location: 'metadata.Title',
            // },
            {
                name: 'Album',
                i18n: 'Assets.Music.Album',
                type: 'metadata',
                location: 'metadata.Album',
            },
            // {
            //     name: 'ISRC', i18n: 'Assets.Music.ISRC', type: 'metadata', location: 'metadata.ISRC',
            // },
            {
                name: 'Created',
                i18n: 'Assets.Created',
                type: 'metadata',
                location: 'createdAt',
            },
        ];
    }

    @action.bound
    fetch = async () => {
        this.fetching = true;
        this.assets.replace([]);
        const params = {};
        if (this.type) {
            params.type = this.type;
        }
        if (this.query) {
            params.q = this.query;
            if (params.q && this.type) {
                // We have a query, pass in fields for type as qprops
                const fieldPaths = this.columnsConfig
                    .filter((f) => f.type === 'metadata')
                    .map((f) => f.location);
                if (fieldPaths && fieldPaths.length) {
                    params.qprops = fieldPaths.join(';');
                }
            }
        }
        if (this.limit) {
            params.limit = this.limit;
        }
        if (this.page) {
            params.offset = this.limit * this.page;
        }
        if (this.queryRelated) {
            params.related = this.queryRelated;
        }
        if (this.queryNotRelated) {
            params.not_related = this.queryNotRelated;
        }

        const response = await this.auth.apiGetWithTotalCount('/api/assets', params);
        runInAction(() => {
            const newAssetsList = response.data.map((item) => AssetModel.fromJS(this.auth, item));
            this.assets.replace(newAssetsList);
            this.totalCount = response.total;
            this.fetched = true;
            this.fetching = false;
        });
    };

    @action.bound
    fetchAsset = async (id) => {
        this.fetching = true;
        this.asset = null;
        const data = await this.auth.apiGet(`/api/assets/${id}`);
        runInAction(() => {
            const newAsset = AssetModel.fromJS(this.auth, data);
            this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
    };

    @action.bound
    createAsset = async (assetData) => {
        this.fetching = true;
        const data = await this.auth.apiCreate('/api/assets/', assetData);
        const newAsset = AssetModel.fromJS(this.auth, data);
        runInAction(() => {
            // console.log(data);
            // TODO Check if this is a good idea...
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
        return newAsset;
    };

    @action.bound
    updateAssetMetadata = async (id, assetMetadata) => {
        this.fetching = true;
        const data = await this.auth.apiUpdate(`/api/assets/${id}`, { metadata: assetMetadata });
        const updatedAsset = AssetModel.fromJS(this.auth, data);
        runInAction(() => {
            // console.log(data);
            // TODO Check if this is a good idea...
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
        return updatedAsset;
    };

    @action.bound
    applyAllStates = async (id) => {
        this.fetching = true;
        const data = await this.auth.apiGet(`/api/assets/${id}/states/apply/all`);
        // const newAsset = AssetModel.fromJS(this.auth, data);
        runInAction(() => {
            // console.log(data);
            // TODO Check if this is a good idea...
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
        return data;
    };

    @action.bound
    async addOrUpdateRelated(id, relatedData) {
        this.fetching = true;
        const data = await this.auth.apiCreate(`/api/assets/${id}/related`, relatedData);
        // const newAsset = AssetModel.fromJS(this.auth, data);
        runInAction(() => {
            // console.log(data);
            // TODO Check if this is a good idea...
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
        return data;
    }

    @action.bound
    async addStorageLocation(id, storageLocation) {
        this.fetching = true;
        const data = await this.auth.apiCreate(`/api/assets/${id}/locations`, storageLocation);
        // const newAsset = AssetModel.fromJS(this.auth, data);
        runInAction(() => {
            // console.log(data);
            // TODO Check if this is a good idea...
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
        return data;
    }

    @action.bound
    storeAssetData = async (assetType, mimeType, assetData) => {
        this.fetching = true;
        // Get Config Set
        const configSet = await this.auth.apiGet('/api/config/sets/');
        // TODO Assume there to take the first configSet and first matching assignment
        const typedStorage = configSet[0].service_assignments.filter(
            (sa) => sa.service_type === 'storage' && sa.element_type === assetType,
        )[0];
        // Get Storage Endpoint and retrieve signed URL
        //
        const storageService = await this.auth.apiGet(`/api/services/${typedStorage.service_id}`);
        // Store the asset
        const storeOperationSuffix = storageService.operations.endpoints.store;
        const storeParams = {
            objectExtension: mimeType === 'image/jpeg' ? 'jpg' : 'png',
            contentType: mimeType,
        };
        const storeOperationInstructions = await this.auth.apiGet(
            `/api/services/${typedStorage.service_id}${storeOperationSuffix}`,
            storeParams,
        );
        // Return the path and details for the asset to link
        switch (storeOperationInstructions.type) {
            case 'presigned_url':
                switch (storeOperationInstructions.method) {
                    case 'PUT':
                        {
                            // Use independent axios client
                            const result = await axios.put(
                                storeOperationInstructions.signed_url,
                                assetData,
                            );
                            if (result.status === 200) {
                                // Success !!
                                const now = new Date();
                                return {
                                    resultCode: result.status,
                                    filename: storeOperationInstructions.filename,
                                    mimeType,
                                    storageLocation: {
                                        type: 'active',
                                        status: 'online',
                                        dmrn: storeOperationInstructions.dmrn,
                                        key: storeOperationInstructions.key,
                                        service_id: typedStorage.service_id,
                                        size: assetData.byteLength,
                                        profile_key: 'original',
                                        mime_type: mimeType,
                                        created: now.toISOString(),
                                        modified: now.toISOString(),
                                        accessed: now.toISOString(),
                                    },
                                };
                            }
                        }
                        break;
                    default:
                        console.error(
                            'UNKNOWN storage instruction method',
                            storeOperationInstructions.method,
                        );
                        break;
                }
                break;
            default:
                console.error('UNKNOWN storage insttruction type', storeOperationInstructions.type);
                break;
        }
        // TODO Handle errors.
        return null;
    };

    @action.bound
    async deleteAsset(id) {
        this.fetching = true;
        const deleted = await this.auth.apiDelete(`/api/assets/${id}`);
        // See https://mobx.js.org/best/actions.html for action
        runInAction(() => {
            // console.log(data);
            // const newAsset = AssetModel.fromJS(this.auth, data);
            // this.asset = newAsset;
            this.fetched = true;
            this.fetching = false;
        });
    }

    @action.bound
    fetchStillPreviews = async (id) => {
        if (!this.fetchingStillPreviews) {
            this.fetchingStillPreviews = true;
            this.assetStillPreviews.replace([]);
            const data = await this.auth.apiGet(`/api/assets/${id}/preview/still`);
            runInAction(() => {
                const newPreviewsList = data.map((preview) =>
                    AssetPreviewModel.fromJS(this.auth, preview),
                );
                this.assetStillPreviews.replace(newPreviewsList);
                this.fetchedStillPreviews = true;
                this.fetchingStillPreviews = false;
            });
        }
    };

    @action.bound
    fetchArworkReplacements = async (id, source, artist, title) => {
        if (!this.fetchingArtworkReplacements) {
            this.fetchingArtworkReplacements = true;
            this.assetArtworkReplacements.replace([]);
            const query = { sources: source, artist, title };
            const data = await this.auth.apiGet(
                `/api/assets/${id}/preview/artworkreplacement?${qs.stringify(query)}`,
            );
            runInAction(() => {
                // const newPreviewsList = data.map(preview => AssetPreviewModel.fromJS(this.auth, preview));
                // TODO Check if without model ok
                this.assetArtworkReplacements.replace(data);
                this.fetchedArtworkReplacements = true;
                this.fetchingArtworkReplacements = false;
            });
        }
    };

    @action.bound
    fetchMetadataReplacements = async (id, source) => {
        if (!this.fetchingMetadata) {
            this.fetchingMetadata = true;
            this.assetMetadataReplacements.replace([]);
            const data = await this.auth.apiGet(
                `/api/assets/${id}/search/metadata?sources=${source}`,
            );
            runInAction(() => {
                // const newPreviewsList = data.map(preview => AssetPreviewModel.fromJS(this.auth, preview));
                // TODO Check if without model ok
                this.assetMetadataReplacements.replace(data);
                this.fetchedMetadata = true;
                this.fetchingMetadata = false;
            });
        }
    };

    @action.bound
    fetchAudioPreviews = async (id) => {
        if (!this.fetchingAudioPreviews) {
            this.fetchingAudioPreviews = true;
            this.assetAudioPreviews.replace([]);
            const data = await this.auth.apiGet(`/api/assets/${id}/preview/audio`);
            runInAction(() => {
                const newPreviewsList = data.map((preview) =>
                    AssetPreviewModel.fromJS(this.auth, preview),
                );
                this.assetAudioPreviews.replace(newPreviewsList);
                this.fetchedAudioPreviews = true;
                this.fetchingAudioPreviews = false;
            });
        }
    };

    @action.bound
    fetchLocations = async (id) => {
        if (!this.fetchingLocations) {
            this.fetchingLocations = true;
            this.locations.replace([]);
            const data = await this.auth.apiGet(`/api/assets/${id}/locations`);
            runInAction(() => {
                const newLocationsList = data.map((location) =>
                    AssetLocationModel.fromJS(this.auth, location),
                );
                this.locations.replace(newLocationsList);
                this.fetchedLocations = true;
                this.fetchingLocations = false;
            });
        }
    };

    @action.bound
    fetchRelated = async (id) => {
        if (!this.fetchingRelated) {
            this.fetchingRelated = true;
            this.related.replace([]);
            const data = await this.auth.apiGet(`/api/assets/${id}/related`);
            // action('update state after fetching related',
            runInAction(() => {
                const newRelatedAssetsList = data.map((related) =>
                    AssetModel.fromJS(this.auth, related.asset, related.name),
                );
                this.related.replace(newRelatedAssetsList);
                this.fetchedRelated = true;
                this.fetchingRelated = false;
            });
            // });
        }
    };

    // syncUpdate() {
    //     // this.auth.socket.syncUpdates('asset', this.assets);
    // }
    //
    // unsyncUpdates() {
    //     // this.auth.socket.unsyncUpdates('asset');
    // }

    // Register a service store to handle service requests of the asset
    registerServiceStore(serviceStore) {
        this.servicesStore = serviceStore;
    }

    toJS() {
        return this.assets.map((a) => a.toJS());
    }

    static fromJS(auth, array) {
        const assetStore = new AssetStore(auth);
        assetStore.assets = array.map((item) => AssetModel.fromJS(item));
        return assetStore;
    }
}
